import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Plus, CheckCircle, X } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';

const inputClass =
  'w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500';

function formatarData(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).substring(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

const STATUS = {
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-500' },
};

export default function AdmissoesTab({ podeEditar }) {
  const [admissoes, setAdmissoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);

  const [form, setForm] = useState({
    nome: '', cpf: '', data_nascimento: '', sexo: '',
    cargo_id: '', departamento_id: '', data_admissao: '',
    salario: '', tipo_contrato: 'clt', jornada_semanal: '44',
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await dpService.listarAdmissoes({ per_page: 50 });
      setAdmissoes(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {
      setErro('Erro ao carregar admissões');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    Promise.all([
      dpService.listarCargos({ per_page: 100 }),
      dpService.listarDepartamentos({ per_page: 100 }),
    ]).then(([c, d]) => {
      setCargos(c.data?.data?.data ?? c.data?.data ?? []);
      setDepartamentos(d.data?.data?.data ?? d.data?.data ?? []);
    }).catch(() => {});
  }, []);

  const setField = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      await dpService.criarAdmissao(form);
      setMostrarForm(false);
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const concluir = async (id) => {
    if (!window.confirm('Concluir admissão? O funcionário será criado automaticamente.')) return;
    try {
      await dpService.concluirAdmissao(id);
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao concluir');
    }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar esta admissão?')) return;
    try {
      await dpService.cancelarAdmissao(id);
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao cancelar');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus size={20} className="text-teal-600" />
          <h2 className="text-base font-bold text-[color:var(--text)]">Admissões</h2>
        </div>
        {podeEditar && (
          <button onClick={() => setMostrarForm(v => !v)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors">
            <Plus size={14} /> Nova Admissão
          </button>
        )}
      </div>

      {erro && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">{erro}</div>}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--surface-muted)]">
          <h3 className="text-sm font-semibold text-[color:var(--text)]">Nova Admissão</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Nome *</label><input required value={form.nome} onChange={setField('nome')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">CPF *</label><input required value={form.cpf} onChange={setField('cpf')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Data Nascimento</label><input type="date" value={form.data_nascimento} onChange={setField('data_nascimento')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Sexo</label><select value={form.sexo} onChange={setField('sexo')} className={inputClass}><option value="">Selecione</option><option value="M">Masculino</option><option value="F">Feminino</option></select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Cargo *</label><select required value={form.cargo_id} onChange={setField('cargo_id')} className={inputClass}><option value="">Selecione</option>{cargos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Departamento</label><select value={form.departamento_id} onChange={setField('departamento_id')} className={inputClass}><option value="">Selecione</option>{departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Data Admissão *</label><input type="date" required value={form.data_admissao} onChange={setField('data_admissao')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Salário *</label><input type="number" step="0.01" required value={form.salario} onChange={setField('salario')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Tipo Contrato</label><select value={form.tipo_contrato} onChange={setField('tipo_contrato')} className={inputClass}><option value="clt">CLT</option><option value="pj">PJ</option><option value="estagio">Estágio</option></select></div>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setMostrarForm(false)} className="px-4 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text-muted)]">Cancelar</button>
            <button type="submit" disabled={salvando} className="px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60">{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      )}

      {carregando ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full" /></div>
      ) : admissoes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserPlus size={40} className="text-[color:var(--text-muted)] opacity-30 mb-3" />
          <p className="text-sm text-[color:var(--text-muted)]">Nenhuma admissão encontrada.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                {['Nome', 'CPF', 'Cargo', 'Data Admissão', 'Salário', 'Status', 'Ações'].map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-[color:var(--text-muted)] whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {admissoes.map(a => {
                const badge = STATUS[a.status] ?? {};
                return (
                  <tr key={a.id} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                    <td className="px-4 py-3 font-medium text-[color:var(--text)]">{a.nome}</td>
                    <td className="px-4 py-3 text-[color:var(--text-muted)]">{a.cpf}</td>
                    <td className="px-4 py-3 text-[color:var(--text-muted)]">{a.cargo?.nome ?? '—'}</td>
                    <td className="px-4 py-3 text-[color:var(--text-muted)]">{formatarData(a.data_admissao)}</td>
                    <td className="px-4 py-3 text-right font-medium text-teal-600">{Number(a.salario || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>{badge.label}</span></td>
                    <td className="px-4 py-3">
                      {podeEditar && a.status === 'pendente' && (
                        <div className="flex gap-1">
                          <button onClick={() => concluir(a.id)} title="Concluir" className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"><CheckCircle size={14} /></button>
                          <button onClick={() => cancelar(a.id)} title="Cancelar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><X size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
